import axios, { AxiosError } from 'axios';

/**
 * Tech Stack Service
 *
 * Handles all tech stack-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Tech Stack endpoints
const TECH_STACK_ENDPOINTS = {
  list: `${API_BASE_URL}/tech-stack`,
  byId: (id: string) => `${API_BASE_URL}/tech-stack/${id}`,
  categories: `${API_BASE_URL}/tech-stack/categories`,
  byCategory: (category: string) => `${API_BASE_URL}/tech-stack/category/${category}`,
  popular: `${API_BASE_URL}/tech-stack/popular`,
  search: `${API_BASE_URL}/tech-stack/search`,
  create: `${API_BASE_URL}/tech-stack`,
  update: (id: string) => `${API_BASE_URL}/tech-stack/${id}`,
  delete: (id: string) => `${API_BASE_URL}/tech-stack/${id}`
};

/**
 * Tech Stack Item interface matching backend
 */
export interface TechStackItem {
  id: string;
  name: string;
  category: string;
  subcategory?: string | null;
  description?: string | null;
  icon_url?: string | null;
  website_url?: string | null;
  documentation_url?: string | null;
  sort_order: number;
  is_popular: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
}

/**
 * Tech Stack Item creation input
 */
export interface CreateTechStackItemInput {
  name: string;
  category: string;
  subcategory?: string;
  description?: string;
  icon_url?: string;
  website_url?: string;
  documentation_url?: string;
  sort_order?: number;
  is_popular?: boolean;
}

/**
 * Tech Stack Item update input
 */
export interface UpdateTechStackItemInput {
  name?: string;
  category?: string;
  subcategory?: string;
  description?: string;
  icon_url?: string;
  website_url?: string;
  documentation_url?: string;
  sort_order?: number;
  is_popular?: boolean;
  is_active?: boolean;
}

/**
 * List tech stack items query parameters
 */
export interface ListTechStackParams {
  category?: string;
  subcategory?: string;
  is_popular?: boolean;
  is_active?: boolean;
  search?: string;
}

/**
 * Search tech stack query parameters
 */
export interface SearchTechStackParams {
  q: string;
}

/**
 * API Response interfaces
 */
export interface TechStackListResponse {
  success: boolean;
  data: TechStackItem[];
  count: number;
}

export interface TechStackResponse {
  success: boolean;
  data: TechStackItem;
}

export interface CategoriesResponse {
  success: boolean;
  data: string[];
  count: number;
}

export interface MessageResponse {
  success: boolean;
  message: string;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * List all tech stack items
 */
export async function listTechStackItems(
  params: ListTechStackParams,
  token: string
): Promise<TechStackItem[]> {
  try {
    const response = await axios.get<TechStackListResponse>(
      TECH_STACK_ENDPOINTS.list,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get all tech stack categories
 */
export async function getTechStackCategories(token: string): Promise<string[]> {
  try {
    const response = await axios.get<CategoriesResponse>(
      TECH_STACK_ENDPOINTS.categories,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get tech stack items by category
 */
export async function getTechStackByCategory(
  category: string,
  token: string
): Promise<TechStackItem[]> {
  try {
    const response = await axios.get<TechStackListResponse>(
      TECH_STACK_ENDPOINTS.byCategory(category),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get popular tech stack items
 */
export async function getPopularTechStack(token: string): Promise<TechStackItem[]> {
  try {
    const response = await axios.get<TechStackListResponse>(
      TECH_STACK_ENDPOINTS.popular,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Search tech stack items
 */
export async function searchTechStack(
  query: string,
  token: string
): Promise<TechStackItem[]> {
  try {
    const response = await axios.get<TechStackListResponse>(
      TECH_STACK_ENDPOINTS.search,
      {
        params: { q: query },
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get tech stack item by ID
 */
export async function getTechStackItemById(
  id: string,
  token: string
): Promise<TechStackItem> {
  try {
    const response = await axios.get<TechStackResponse>(
      TECH_STACK_ENDPOINTS.byId(id),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Create a new tech stack item (Platform Admin only)
 */
export async function createTechStackItem(
  data: CreateTechStackItemInput,
  token: string
): Promise<TechStackItem> {
  try {
    const response = await axios.post<TechStackResponse>(
      TECH_STACK_ENDPOINTS.create,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update tech stack item (Platform Admin only)
 */
export async function updateTechStackItem(
  id: string,
  data: UpdateTechStackItemInput,
  token: string
): Promise<TechStackItem> {
  try {
    const response = await axios.put<TechStackResponse>(
      TECH_STACK_ENDPOINTS.update(id),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Delete tech stack item (Platform Admin only)
 */
export async function deleteTechStackItem(id: string, token: string): Promise<void> {
  try {
    await axios.delete<MessageResponse>(TECH_STACK_ENDPOINTS.delete(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  listTechStackItems,
  getTechStackCategories,
  getTechStackByCategory,
  getPopularTechStack,
  searchTechStack,
  getTechStackItemById,
  createTechStackItem,
  updateTechStackItem,
  deleteTechStackItem
};
